import type { IPluginLoader } from '@/application/ports/IPluginLoader';
import type { ActivationPlugin } from '@hub/plugin-sdk';
import type { PluginRemoteConfig } from '@/domain/entities/PluginRemoteConfig';

export type LoadPluginResult =
  | { success: true; plugin: ActivationPlugin }
  // `offline` (HUB-237, CA-16): mesma heurística de `HttpEventConfigRepository`
  // — `TypeError` é o que o runtime de módulos/`fetch` lança quando a
  // requisição não pôde nem ser tentada (sem conectividade, DNS, CORS
  // bloqueado). Usado por `usePluginLoadRetry` para escolher entre a
  // mensagem final "Sem conexão..." e a genérica "Não foi possível
  // carregar..." — não afeta a lógica de retry em si, que é igual nos dois
  // casos.
  | { success: false; error: string; offline: boolean };

/**
 * Use case responsável por carregar um plugin remoto via Module Federation.
 * Encapsula o tratamento de erro da chamada ao loader, mantendo a camada de
 * apresentação livre de try/catch de infraestrutura.
 */
export class LoadPluginUseCase {
  constructor(private readonly loader: IPluginLoader) {}

  async execute(config: PluginRemoteConfig): Promise<LoadPluginResult> {
    try {
      const plugin = await this.loader.load(config);
      return { success: true, plugin };
    } catch (error) {
      // Zero erros silenciosos (CLAUDE.md): a UI só expõe uma mensagem
      // genérica (`LoadPluginResult.error`), então o erro real precisa
      // aparecer em algum lugar observável — sem isto, uma falha real de
      // carregamento (ex.: HUB-273) fica invisível sem instrumentação manual.
      console.error('[LoadPluginUseCase] Falha ao carregar plugin remoto:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido';
      return { success: false, error: message, offline: error instanceof TypeError };
    }
  }
}
